<template>
  <div>
    <NavBar />
    <div class="container">
      <div class="row d-print-none">
        <div class="col">
          <h1>
            Investigations <small>{{ organizationDisplayName }}</small>
          </h1>
        </div>
      </div>
      <div class="row d-print-none">
        <div class="col">
          <div class="py-4">
            <form class="form-inline w-100" @submit.prevent>
              <div class="input-group d-flex flex-fill">
                <input type="text" class="form-control" placeholder="Search by investigation name" v-model="query" />
                <div class="input-group-append">
                  <button type="button" class="btn btn-primary" @click="search">
                    <fa icon="search" />
                    <span class="ml-2">Search</span>
                  </button>
                  <button type="button" class="btn btn-outline-primary" @click="refresh">
                    <fa icon="redo" />
                    <span class="ml-2">Refresh</span>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
        <div class="col-md-auto">
          <div class="d-flex py-4">
            <div class="ml-auto">
              <button type="button" class="btn btn-outline-primary mr-2" @click="listExport" :disabled="isExporting">
                <fa v-if="isExporting" icon="circle-notch" spin />
                <fa v-if="!isExporting" icon="file-excel" />
                <span class="ml-2">Export List</span>
              </button>
              <button v-if="canCreateInvestigation" type="button" class="btn btn-primary" @click="createInvestigation">
                <fa icon="plus" />
                <span class="ml-2">Create Investigation</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="row d-print-none">
        <div class="col">
          <h5 v-if="page.total" class="pb-2">Results: {{ page.total.toLocaleString() }}</h5>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <div class="table-responsive">
            <table class="table table-striped">
              <tbody>
                <tr v-if="isLoading" class="text-center text-muted">
                  <td><fa icon="circle-notch" spin /> Loading...</td>
                </tr>
                <tr v-if="error" class="text-center text-muted">
                  <td>Sorry. There was an error. Please try again.</td>
                </tr>
                <tr v-if="page.total === 0" class="text-center text-muted">
                  <td>No results</td>
                </tr>
                <tr v-for="item in page.items" :key="item.id">
                  <Row :organizationId="organizationId" :investigation="item" />
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div v-if="needsPaging" class="row">
        <div class="col">
          <div class="row justify-content-between p-3">
            <button type="button" class="btn btn-outline-dark" @click="prev" :disabled="!hasPrev">Previous</button>
            <button type="button" class="btn btn-outline-dark" @click="next" :disabled="!hasNext">Next</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import debounce from "debounce";
import NavBar from "@/views/reports/NavBar.vue";
import Row from "@/views/reports/investigations/list-investigations/Row.vue";

export default {
  components: {
    NavBar,
    Row,
  },
  props: {
    organizationId: String,
  },
  data() {
    return {
      isLoading: false,
      isExporting: false,

      skip: 0,
      take: 25,
      query: null,

      page: {},
      error: null,
    };
  },
  computed: {
    ...mapGetters("organization", ["organization"]),
    ...mapGetters("role", ["isVerensicsAdministrator", "isOrganizationAdministrator", "isInvestigationCreator", "isInvestigationProctor"]),
    organizationDisplayName() {
      if (!this.isVerensicsAdministrator) return null;
      if (this.organizationId === this.organization.id) return "(All)";
      return this.page && this.page.items && this.page.items.length ? `(${this.page.items[0].organizationDisplayName})` : null;
    },
    hasPrev() {
      if (!this.page) return false;
      return this.skip > 0;
    },
    hasNext() {
      if (!this.page) return false;
      return this.skip + this.take < this.page.total;
    },
    needsPaging() {
      return this.hasNext || this.hasPrev;
    },
    queryString() {
      let url = `skip=${this.skip}&take=${this.take}`;
      if (this.query) url += `&query=${encodeURIComponent(this.query)}`;
      return url;
    },
    canCreateInvestigation() {
      return this.isOrganizationAdministrator || this.isInvestigationCreator;
    },
  },
  watch: {
    queryString() {
      this.load();
    },
    query() {
      this.skip = 0;
    },
    user() {
      this.skip = 0;
      this.load();
    },
  },
  created() {
    // query strings
    this.skip = this.$route.query.skip ? parseInt(this.$route.query.skip) : this.skip;
    this.take = this.$route.query.take ? parseInt(this.$route.query.take) : this.take;
    this.query = this.$route.query.query || this.query;

    this.load = debounce(this.load, 350);
    this.load();
  },
  methods: {
    load() {
      this.page = {};
      this.error = null;
      this.isLoading = true;
      this.$axios
        .get(`/v1/organizations/${this.organizationId}/investigations?${this.queryString}`)
        .then(res => (this.page = res.data))
        .catch(err => (this.error = err))
        .finally(() => {
          this.isLoading = false;
        });
    },
    prev() {
      this.skip = Math.max(0, this.skip - this.take);
    },
    next() {
      this.skip += this.take;
    },
    refresh() {
      this.load();
    },
    search() {
      this.load();
    },
    listExport() {
      this.isExporting = true;
      this.$axios
        .post(`/v1/organizations/${this.organizationId}/investigations/list/export?${this.queryString}`)
        .then(res => {
          const link = document.createElement("a");
          link.href = res.data.url;
          link.click();
          link.remove();
        })
        .finally(() => (this.isExporting = false));
    },
    createInvestigation() {
      this.$router.push(`/organizations/${this.organizationId}/investigations/create`);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/base.scss";

table {
  @extend .border;

  tr {
    @extend .border-bottom;
  }
}
</style>
